import React, { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import Menu from './Header/Menu';
import TeamPlanningPage from './Pages/TeamPlanningPage';
import RnDPlanningPage from './Pages/RnDPlanningPage';
import TriagePage from './Pages/TriagePage';
import TimeTrackingPage from './Pages/TimeTrackingPage';
import WorkInProgressPage from './Pages/WorkInProgressPage';
import TimeAdministrationPage from './Pages/TimeAdministrationPage';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import LoginButton from './LoginButton';
import { AzureDevOpsService, ProjectWorkItemData, TeamSettingsIteration } from './services/AzureDevOpsService';
import { TEAMS_LIST, PLANNING_PROJECTS } from './Common/Constants';
import { PublicClientApplication } from '@azure/msal-browser';

function AuthenticatedApp() {
  const [userName, setUserName] = useState("Please login");
  const [useAdvancedMode, setUseAdvancedMode] = useState(localStorage.getItem("UseAdvancedMode") === "true");
  const [selectedPriorityDriver, setSelectedPriorityDriver] = useState<string>(localStorage.getItem("SelectedPriorityDriver") || "");
  const [projectWorkItemData, setProjectWorkItemData] = useState<ProjectWorkItemData[]>([]);
  const [isWorkItemDataLoaded, setIsWorkItemDataLoaded] = useState(false);
  const [usersData, setUsersData] = useState<any[]>([]);
  const [isUsersDataLoaded, setIsUsersDataLoaded] = useState(false);
  const [capexOptions, setCapexOptions] = useState<string[]>([]);
  const [isCapexDataLoaded, setIsCapexDataLoaded] = useState(false);
  const [govProjectOptions, setGovProjectOptions] = useState<string[]>([]);
  const [isGovProjectDataLoaded, setIsGovProjectDataLoaded] = useState(false);
  const [priorityDriverOptions, setPriorityDriverOptions] = useState<string[]>([]);
  const [isPriorityDriverDataLoaded, setIsPriorityDriverDataLoaded] = useState(false);
  const [demoOptions, setDemoOptions] = useState<string[]>([]);
  const [isDemoDataLoaded, setIsDemoDataLoaded] = useState(false);
  const [initiativeOptions, setInitiativeOptions] = useState<string[]>([]);
  const [isInitiativeDataLoaded, setIsInitiativeDataLoaded] = useState(false);
  const [productOptions, setProductOptions] = useState<string[]>([]);
  const [isProductDataLoaded, setIsProductDataLoaded] = useState(false);
  const [investmentClassOptions, setInvestmentClassOptions] = useState<string[]>([]);
  const [isInvestmentClassDataLoaded, setIsInvestmentClassDataLoaded] = useState(false);
  const [iterationsData, setIterationsData] = useState<TeamSettingsIteration[]>([]);
  const [isIterationsDataLoaded, setIsIterationsDataLoaded] = useState(false);
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();

  useEffect(() => {
    const fetchDevOpsUserName = async () => {
      if (accounts.length > 0) {
        try {
          const devOpsService = new AzureDevOpsService(instance as PublicClientApplication, accounts[0]);
          const url = `https://dev.azure.com/sensio/_apis/connectionData?connectOptions=1`;
          const accessToken = await devOpsService.getAccessToken();
          if (!accessToken) return;
          const response = await fetch(url, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Accept': 'application/json',
            },
          });
          if (!response.ok) return;
          const profileData = await response.json();
          if (
            profileData &&
            profileData.authenticatedUser &&
            profileData.authenticatedUser.providerDisplayName
          ) {
            setUserName(profileData.authenticatedUser.providerDisplayName);
          }
        } catch (error) {
          // Optionally handle error
        }
      }
    };
    fetchDevOpsUserName();
  }, [instance, accounts]);

  useEffect(() => {
    const fetchAllWorkItemData = async () => {
      if (accounts.length > 0 && !isWorkItemDataLoaded) {
        setIsWorkItemDataLoaded(true);
        
        try {
          const devOpsService = new AzureDevOpsService(instance as PublicClientApplication, accounts[0]);
          const organization = 'sensio';
          
          const cacheKey = 'workItemData';
          const cacheTimestampKey = 'workItemDataTimestamp';
          const cacheExpiry = 24 * 60 * 60 * 1000;
          
          const cachedData = localStorage.getItem(cacheKey);
          const cacheTimestamp = localStorage.getItem(cacheTimestampKey);
          
          if (cachedData && cacheTimestamp) {
            const isExpired = Date.now() - parseInt(cacheTimestamp) > cacheExpiry;
            if (!isExpired) {
              setProjectWorkItemData(JSON.parse(cachedData));
              console.log('Loaded work item data from cache');
              return;
            }
          }
          
          console.log('Fetching work item data in background...');
          
          setTimeout(async () => {
            try {
              const data = await devOpsService.fetchAllProjectWorkItemData(organization);
              
              setProjectWorkItemData(data);
              if (data.length > 0) {
                try {
                  localStorage.setItem(cacheKey, JSON.stringify(data));
                  localStorage.setItem(cacheTimestampKey, Date.now().toString());
                  console.log('Background fetch completed - work item data updated');
                } catch (storageError) {
                  console.warn('Failed to cache work item data due to storage quota:', storageError);
                  try {
                    localStorage.removeItem(cacheKey);
                    localStorage.removeItem(cacheTimestampKey);
                  } catch (clearError) {
                    console.warn('Failed to clear old cache data:', clearError);
                  }
                }
              }
            } catch (backgroundError) {
              console.warn('Background fetch of work item data failed:', backgroundError);
            }
          }, 100);
          
        } catch (error) {
          console.error('Error initializing work item data fetch:', error);
        }
      }
    };
    
    fetchAllWorkItemData();
  }, [accounts, instance, isWorkItemDataLoaded]);

  useEffect(() => {
    const fetchAllUsersData = async () => {
      if (accounts.length > 0 && !isUsersDataLoaded) {
        setIsUsersDataLoaded(true);
        
        try {
          const devOpsService = new AzureDevOpsService(instance as PublicClientApplication, accounts[0]);
          const organization = 'sensio';
          
          const cacheKey = 'usersData';
          const cacheTimestampKey = 'usersDataTimestamp';
          const cacheExpiry = 24 * 60 * 60 * 1000;
          
          const cachedData = localStorage.getItem(cacheKey);
          const cacheTimestamp = localStorage.getItem(cacheTimestampKey);
          
          if (cachedData && cacheTimestamp) {
            const isExpired = Date.now() - parseInt(cacheTimestamp) > cacheExpiry;
            if (!isExpired) {
              setUsersData(JSON.parse(cachedData));
              console.log('Loaded users data from cache');
              return;
            }
          }
          
          console.log('Fetching users data in background...');
          
          setTimeout(async () => {
            try {
              const data = await devOpsService.fetchAllUsers(organization);
              
              setUsersData(data);
              if (data.length > 0) {
                try {
                  localStorage.setItem(cacheKey, JSON.stringify(data));
                  localStorage.setItem(cacheTimestampKey, Date.now().toString());
                  console.log('Background fetch completed - users data updated');
                } catch (storageError) {
                  console.warn('Failed to cache users data due to storage quota:', storageError);
                  try {
                    localStorage.removeItem(cacheKey);
                    localStorage.removeItem(cacheTimestampKey);
                  } catch (clearError) {
                    console.warn('Failed to clear old cache data:', clearError);
                  }
                }
              }
            } catch (backgroundError) {
              console.warn('Background fetch of users data failed:', backgroundError);
            }
          }, 100);
          
        } catch (error) {
          console.error('Error initializing users data fetch:', error);
        }
      }
    };
    
    fetchAllUsersData();
  }, [accounts, instance, isUsersDataLoaded]);

  useEffect(() => {
    const fetchCapexData = async () => {
      if (accounts.length > 0 && !isCapexDataLoaded) {
        setIsCapexDataLoaded(true);
        
        try {
          const devOpsService = new AzureDevOpsService(instance as PublicClientApplication, accounts[0]);
          const organization = 'sensio';
          const project = 'RnD Planning';
          
          const cacheKey = 'capexData';
          const cacheTimestampKey = 'capexDataTimestamp';
          const cacheExpiry = 24 * 60 * 60 * 1000;
          
          const cachedData = localStorage.getItem(cacheKey);
          const cacheTimestamp = localStorage.getItem(cacheTimestampKey);
          
          if (cachedData && cacheTimestamp) {
            const isExpired = Date.now() - parseInt(cacheTimestamp) > cacheExpiry;
            if (!isExpired) {
              setCapexOptions(JSON.parse(cachedData));
              console.log('Loaded CapEx data from cache');
              return;
            }
          }
          
          console.log('Fetching CapEx data in background...');
          
          setTimeout(async () => {
            try {
              const data = await devOpsService.fetchCapexOptions(organization, project);
              
              setCapexOptions(data);
              if (data.length > 0) {
                try {
                  localStorage.setItem(cacheKey, JSON.stringify(data));
                  localStorage.setItem(cacheTimestampKey, Date.now().toString());
                  console.log('Background fetch completed - CapEx data updated');
                } catch (storageError) {
                  console.warn('Failed to cache CapEx data due to storage quota:', storageError);
                  try {
                    localStorage.removeItem(cacheKey);
                    localStorage.removeItem(cacheTimestampKey);
                  } catch (clearError) {
                    console.warn('Failed to clear old cache data:', clearError);
                  }
                }
              }
            } catch (backgroundError) {
              console.warn('Background fetch of CapEx data failed:', backgroundError);
            }
          }, 100);
          
        } catch (error) {
          console.error('Error initializing CapEx data fetch:', error);
        }
      }
    };
    
    fetchCapexData();
  }, [accounts, instance, isCapexDataLoaded]);

  useEffect(() => {
    const fetchGovProjectData = async () => {
      if (accounts.length > 0 && !isGovProjectDataLoaded) {
        setIsGovProjectDataLoaded(true);
        
        try {
          const devOpsService = new AzureDevOpsService(instance as PublicClientApplication, accounts[0]);
          const organization = 'sensio';
          const project = 'RnD Planning';
          
          const cacheKey = 'govProjectData';
          const cacheTimestampKey = 'govProjectDataTimestamp';
          const cacheExpiry = 24 * 60 * 60 * 1000;
          
          const cachedData = localStorage.getItem(cacheKey);
          const cacheTimestamp = localStorage.getItem(cacheTimestampKey);
          
          if (cachedData && cacheTimestamp) {
            const isExpired = Date.now() - parseInt(cacheTimestamp) > cacheExpiry;
            if (!isExpired) {
              setGovProjectOptions(JSON.parse(cachedData));
              console.log('Loaded Gov Project data from cache');
              return;
            }
          }
          
          console.log('Fetching Gov Project data in background...');
          
          setTimeout(async () => {
            try {
              const data = await devOpsService.fetchGovProjectOptions(organization, project);
              
              setGovProjectOptions(data);
              if (data.length > 0) {
                try {
                  localStorage.setItem(cacheKey, JSON.stringify(data));
                  localStorage.setItem(cacheTimestampKey, Date.now().toString());
                  console.log('Background fetch completed - Gov Project data updated');
                } catch (storageError) {
                  console.warn('Failed to cache Gov Project data due to storage quota:', storageError);
                  try {
                    localStorage.removeItem(cacheKey);
                    localStorage.removeItem(cacheTimestampKey);
                  } catch (clearError) {
                    console.warn('Failed to clear old cache data:', clearError);
                  }
                }
              }
            } catch (backgroundError) {
              console.warn('Background fetch of Gov Project data failed:', backgroundError);
            }
          }, 100);
          
        } catch (error) {
          console.error('Error initializing Gov Project data fetch:', error);
        }
      }
    };
    
    fetchGovProjectData();
  }, [accounts, instance, isGovProjectDataLoaded]);

  useEffect(() => {
    const fetchPriorityDriverData = async () => {
      if (accounts.length > 0 && !isPriorityDriverDataLoaded) {
        setIsPriorityDriverDataLoaded(true);
        
        try {
          const devOpsService = new AzureDevOpsService(instance as PublicClientApplication, accounts[0]);
          const organization = 'sensio';
          const project = 'RnD Planning';
          
          const cacheKey = 'priorityDriverData';
          const cacheTimestampKey = 'priorityDriverDataTimestamp';
          const cacheExpiry = 24 * 60 * 60 * 1000;
          
          const cachedData = localStorage.getItem(cacheKey);
          const cacheTimestamp = localStorage.getItem(cacheTimestampKey);
          
          if (cachedData && cacheTimestamp) {
            const isExpired = Date.now() - parseInt(cacheTimestamp) > cacheExpiry;
            if (!isExpired) {
              setPriorityDriverOptions(JSON.parse(cachedData));
              console.log('Loaded Priority Driver data from cache');
              return;
            }
          }
          
          console.log('Fetching Priority Driver data in background...');
          
          setTimeout(async () => {
            try {
              const data = await devOpsService.fetchPriorityDriverOptions(organization, project);
              
              setPriorityDriverOptions(data);
              if (data.length > 0) {
                try {
                  localStorage.setItem(cacheKey, JSON.stringify(data));
                  localStorage.setItem(cacheTimestampKey, Date.now().toString());
                  console.log('Background fetch completed - Priority Driver data updated');
                } catch (storageError) {
                  console.warn('Failed to cache Priority Driver data due to storage quota:', storageError);
                  try {
                    localStorage.removeItem(cacheKey);
                    localStorage.removeItem(cacheTimestampKey);
                  } catch (clearError) {
                    console.warn('Failed to clear old cache data:', clearError);
                  }
                }
              }
            } catch (backgroundError) {
              console.warn('Background fetch of Priority Driver data failed:', backgroundError);
            }
          }, 100);
          
        } catch (error) {
          console.error('Error initializing Priority Driver data fetch:', error);
        }
      }
    };
    
    fetchPriorityDriverData();
  }, [accounts, instance, isPriorityDriverDataLoaded]);

  useEffect(() => {
    const fetchDemoData = async () => {
      if (accounts.length > 0 && !isDemoDataLoaded) {
        setIsDemoDataLoaded(true);
        
        try {
          const devOpsService = new AzureDevOpsService(instance as PublicClientApplication, accounts[0]);
          const organization = 'sensio';
          const project = 'RnD Planning';
          
          const cacheKey = 'demoData';
          const cacheTimestampKey = 'demoDataTimestamp';
          const cacheExpiry = 24 * 60 * 60 * 1000;
          
          const cachedData = localStorage.getItem(cacheKey);
          const cacheTimestamp = localStorage.getItem(cacheTimestampKey);
          
          if (cachedData && cacheTimestamp) {
            const isExpired = Date.now() - parseInt(cacheTimestamp) > cacheExpiry;
            if (!isExpired) {
              setDemoOptions(JSON.parse(cachedData));
              console.log('Loaded Demo data from cache');
              return;
            }
          }
          
          console.log('Fetching Demo data in background...');
          
          setTimeout(async () => {
            try {
              const data = await devOpsService.fetchDemoOptions(organization, project);
              
              setDemoOptions(data);
              if (data.length > 0) {
                try {
                  localStorage.setItem(cacheKey, JSON.stringify(data));
                  localStorage.setItem(cacheTimestampKey, Date.now().toString());
                  console.log('Background fetch completed - Demo data updated');
                } catch (storageError) {
                  console.warn('Failed to cache Demo data due to storage quota:', storageError);
                  try {
                    localStorage.removeItem(cacheKey);
                    localStorage.removeItem(cacheTimestampKey);
                  } catch (clearError) {
                    console.warn('Failed to clear old cache data:', clearError);
                  }
                }
              }
            } catch (backgroundError) {
              console.warn('Background fetch of Demo data failed:', backgroundError);
            }
          }, 100);
          
        } catch (error) {
          console.error('Error initializing Demo data fetch:', error);
        }
      }
    };
    
    fetchDemoData();
  }, [accounts, instance, isDemoDataLoaded]);

  useEffect(() => {
    const fetchInitiativeData = async () => {
      if (accounts.length > 0 && !isInitiativeDataLoaded) {
        setIsInitiativeDataLoaded(true);
        
        try {
          const devOpsService = new AzureDevOpsService(instance as PublicClientApplication, accounts[0]);
          const organization = 'sensio';
          
          const cacheKey = 'initiativeData';
          const cacheTimestampKey = 'initiativeDataTimestamp';
          const cacheExpiry = 24 * 60 * 60 * 1000;
          
          const cachedData = localStorage.getItem(cacheKey);
          const cacheTimestamp = localStorage.getItem(cacheTimestampKey);
          
          if (cachedData && cacheTimestamp) {
            const isExpired = Date.now() - parseInt(cacheTimestamp) > cacheExpiry;
            if (!isExpired) {
              setInitiativeOptions(JSON.parse(cachedData));
              console.log('Loaded Initiative data from cache');
              return;
            }
          }
          
          console.log('Fetching Initiative data in background...');
          
          setTimeout(async () => {
            try {
              const data = await devOpsService.fetchInitiativeOptions(organization);
              
              setInitiativeOptions(data);
              if (data.length > 0) {
                try {
                  localStorage.setItem(cacheKey, JSON.stringify(data));
                  localStorage.setItem(cacheTimestampKey, Date.now().toString());
                  console.log('Background fetch completed - Initiative data updated');
                } catch (storageError) {
                  console.warn('Failed to cache Initiative data due to storage quota:', storageError);
                  try {
                    localStorage.removeItem(cacheKey);
                    localStorage.removeItem(cacheTimestampKey);
                  } catch (clearError) {
                    console.warn('Failed to clear old cache data:', clearError);
                  }
                }
              }
            } catch (backgroundError) {
              console.warn('Background fetch of Initiative data failed:', backgroundError);
            }
          }, 100);
          
        } catch (error) {
          console.error('Error initializing Initiative data fetch:', error);
        }
      }
    };
    
    fetchInitiativeData();
  }, [accounts, instance, isInitiativeDataLoaded]);

  useEffect(() => {
    const fetchProductData = async () => {
      if (accounts.length > 0 && !isProductDataLoaded) {
        setIsProductDataLoaded(true);
        
        try {
          const devOpsService = new AzureDevOpsService(instance as PublicClientApplication, accounts[0]);
          const organization = 'sensio';
          const project = 'RnD Planning';
          
          const cacheKey = 'productData';
          const cacheTimestampKey = 'productDataTimestamp';
          const cacheExpiry = 24 * 60 * 60 * 1000;
          
          const cachedData = localStorage.getItem(cacheKey);
          const cacheTimestamp = localStorage.getItem(cacheTimestampKey);
          
          if (cachedData && cacheTimestamp) {
            const isExpired = Date.now() - parseInt(cacheTimestamp) > cacheExpiry;
            if (!isExpired) {
              setProductOptions(JSON.parse(cachedData));
              console.log('Loaded Product data from cache');
              return;
            }
          }
          
          console.log('Fetching Product data in background...');
          
          setTimeout(async () => {
            try {
              const data = await devOpsService.fetchProductOptions(organization, project);
              
              setProductOptions(data);
              if (data.length > 0) {
                try {
                  localStorage.setItem(cacheKey, JSON.stringify(data));
                  localStorage.setItem(cacheTimestampKey, Date.now().toString());
                  console.log('Background fetch completed - Product data updated');
                } catch (storageError) {
                  console.warn('Failed to cache Product data due to storage quota:', storageError);
                  try {
                    localStorage.removeItem(cacheKey);
                    localStorage.removeItem(cacheTimestampKey);
                  } catch (cleanupError) {
                    console.warn('Failed to cleanup Product cache:', cleanupError);
                  }
                }
              }
            } catch (backgroundError) {
              console.warn('Background fetch of Product data failed:', backgroundError);
            }
          }, 100);
          
        } catch (error) {
          console.error('Error initializing Product data fetch:', error);
        }
      }
    };
    
    fetchProductData();
  }, [accounts, instance, isProductDataLoaded]);

  useEffect(() => {
    const fetchInvestmentClassData = async () => {
      if (accounts.length > 0 && !isInvestmentClassDataLoaded) {
        setIsInvestmentClassDataLoaded(true);
        
        try {
          const devOpsService = new AzureDevOpsService(instance as PublicClientApplication, accounts[0]);
          const organization = 'sensio';
          const project = 'RnD Planning';
          
          const cacheKey = 'investmentClassData';
          const cacheTimestampKey = 'investmentClassDataTimestamp';
          const cacheExpiry = 24 * 60 * 60 * 1000;
          
          const cachedData = localStorage.getItem(cacheKey);
          const cacheTimestamp = localStorage.getItem(cacheTimestampKey);
          
          if (cachedData && cacheTimestamp) {
            const isExpired = Date.now() - parseInt(cacheTimestamp) > cacheExpiry;
            if (!isExpired) {
              setInvestmentClassOptions(JSON.parse(cachedData));
              console.log('Loaded Investment Class data from cache');
              return;
            }
          }
          
          console.log('Fetching Investment Class data in background...');
          
          setTimeout(async () => {
            try {
              const data = await devOpsService.fetchInvestmentClassOptions(organization, project);
              
              setInvestmentClassOptions(data);
              if (data.length > 0) {
                try {
                  localStorage.setItem(cacheKey, JSON.stringify(data));
                  localStorage.setItem(cacheTimestampKey, Date.now().toString());
                  console.log('Background fetch completed - Investment Class data updated');
                } catch (storageError) {
                  console.warn('Failed to cache Investment Class data due to storage quota:', storageError);
                  try {
                    localStorage.removeItem(cacheKey);
                    localStorage.removeItem(cacheTimestampKey);
                  } catch (cleanupError) {
                    console.warn('Failed to cleanup Investment Class cache:', cleanupError);
                  }
                }
              }
            } catch (backgroundError) {
              console.warn('Background fetch of Investment Class data failed:', backgroundError);
            }
          }, 100);
          
        } catch (error) {
          console.error('Error initializing Investment Class data fetch:', error);
        }
      }
    };
    
    fetchInvestmentClassData();
  }, [accounts, instance, isInvestmentClassDataLoaded]);

  useEffect(() => {
    const fetchIterationsData = async () => {
      if (accounts.length > 0 && !isIterationsDataLoaded) {
        setIsIterationsDataLoaded(true);
        
        try {
          const devOpsService = new AzureDevOpsService(instance as PublicClientApplication, accounts[0]);
          const organization = 'sensio';
          
          const cacheKey = 'iterationsData';
          const cacheTimestampKey = 'iterationsDataTimestamp';
          const cacheExpiry = 10 * 60 * 1000; // 10 minutes in milliseconds
          
          const cachedData = localStorage.getItem(cacheKey);
          const cacheTimestamp = localStorage.getItem(cacheTimestampKey);
          
          if (cachedData && cacheTimestamp) {
            const isExpired = Date.now() - parseInt(cacheTimestamp) > cacheExpiry;
            if (!isExpired) {
              const parsedData = JSON.parse(cachedData);
              setIterationsData(parsedData);
              console.log('Loaded iterations data from cache:', parsedData);
              return;
            }
          }
          
          console.log('Fetching iterations data from Azure DevOps API...');
          
          setTimeout(async () => {
            try {
              const usedProjects = [
                ...TEAMS_LIST.map(team => team.project).filter(project => project && project.trim() !== ''),
                ...PLANNING_PROJECTS
              ];
              
              const uniqueProjects = usedProjects.filter((project, index, array) => array.indexOf(project) === index);
              const allIterations: any[] = [];
              
              console.log('Fetching iterations for used projects:', uniqueProjects);
              
              for (const projectName of uniqueProjects) {
                try {
                  const iterations = await devOpsService.fetchIterations(organization, projectName);
                  const projectIterations = iterations.map(iteration => ({
                    ...iteration,
                    projectName: projectName
                  }));
                  allIterations.push(...projectIterations);
                } catch (projectError) {
                  console.warn(`Failed to fetch iterations for project ${projectName}:`, projectError);
                }
              }
              
              const now = new Date();
              const fortyDaysAgo = new Date(now.getTime() - (40 * 24 * 60 * 60 * 1000));
              const sixtyDaysFromNow = new Date(now.getTime() + (60 * 24 * 60 * 60 * 1000));
              
              const filteredIterations = allIterations.filter(iteration => {
                const startDate = iteration.attributes?.startDate ? new Date(iteration.attributes.startDate) : null;
                const endDate = iteration.attributes?.finishDate ? new Date(iteration.attributes.finishDate) : null;
                
                const hasValidEndDate = endDate && endDate >= fortyDaysAgo;
                const hasValidStartDate = startDate && startDate <= sixtyDaysFromNow;
                
                return hasValidEndDate && hasValidStartDate;
              });
              
              console.log(`Filtered ${allIterations.length} iterations to ${filteredIterations.length} based on date criteria`);
              console.log('Date filter criteria:', {
                fortyDaysAgo: fortyDaysAgo.toISOString(),
                sixtyDaysFromNow: sixtyDaysFromNow.toISOString()
              });
              
              setIterationsData(filteredIterations);
              console.log('Fetched and filtered iterations data from API:', filteredIterations);
              
              if (filteredIterations.length > 0) {
                try {
                  localStorage.setItem(cacheKey, JSON.stringify(filteredIterations));
                  localStorage.setItem(cacheTimestampKey, Date.now().toString());
                  console.log('Filtered iterations data cached successfully');
                } catch (storageError) {
                  console.warn('Failed to cache iterations data due to storage quota:', storageError);
                  try {
                    localStorage.removeItem(cacheKey);
                    localStorage.removeItem(cacheTimestampKey);
                  } catch (clearError) {
                    console.warn('Failed to clear old cache data:', clearError);
                  }
                }
              }
            } catch (backgroundError) {
              console.warn('Background fetch of iterations data failed:', backgroundError);
            }
          }, 100);
          
        } catch (error) {
          console.error('Error initializing iterations data fetch:', error);
        }
      }
    };
    
    fetchIterationsData();
  }, [accounts, instance, isIterationsDataLoaded]);

  if (!isAuthenticated) {
    return (<LoginButton />)
  }

  return (
    <>
      <Menu 
        userName={userName} 
        useAdvancedMode={useAdvancedMode} // Default to false if not provided
        setUseAdvancedMode={(advancedMode : boolean) => {
          setUseAdvancedMode(advancedMode);
          try {
            localStorage.setItem("UseAdvancedMode", advancedMode.toString());
          } catch (storageError) {
            console.warn('Failed to save UseAdvancedMode setting due to storage quota:', storageError);
          }
        }} // Default to a no-op function if not provided
        priorityDriverOptions={priorityDriverOptions}
        selectedPriorityDriver={selectedPriorityDriver}
        setSelectedPriorityDriver={(value: string) => {
          setSelectedPriorityDriver(value);
          try {
            localStorage.setItem("SelectedPriorityDriver", value);
          } catch (storageError) {
            console.warn('Failed to save SelectedPriorityDriver setting due to storage quota:', storageError);
          }
        }}
        onLogout={() => {
          console.log("User logged out");
          instance.logoutRedirect({
            postLogoutRedirectUri: window.location.origin,
          }); 
        // Add logout logic here
        }} 
      />
      {/* @ts-ignore */}
      <Routes>
        {/* @ts-ignore */}
        <Route path="/team-planning" element={
          <TeamPlanningPage 
            useAdvancedMode={useAdvancedMode} 
            projectWorkItemData={projectWorkItemData} 
            usersData={usersData} 
            capexOptions={capexOptions} 
            govProjectOptions={govProjectOptions} 
            priorityDriverOptions={priorityDriverOptions} 
            demoOptions={demoOptions} 
            iterationsData={iterationsData} 
            productOptions={productOptions} 
            investmentClassOptions={investmentClassOptions}
            selectedPriorityDriver={selectedPriorityDriver}
            setSelectedPriorityDriver={(value: string) => {
              setSelectedPriorityDriver(value);
              try {
                localStorage.setItem("SelectedPriorityDriver", value);
              } catch (storageError) {
                console.warn('Failed to save SelectedPriorityDriver setting due to storage quota:', storageError);
              }
            }}
          />
        } />
        {/* @ts-ignore */}
        <Route path="/rnd-planning" element={
          <RnDPlanningPage 
            useAdvancedMode={useAdvancedMode} 
            projectWorkItemData={projectWorkItemData} 
            usersData={usersData} 
            capexOptions={capexOptions} 
            govProjectOptions={govProjectOptions} 
            priorityDriverOptions={priorityDriverOptions} 
            demoOptions={demoOptions} 
            iterationsData={iterationsData} 
            productOptions={productOptions} 
            investmentClassOptions={investmentClassOptions}
            selectedPriorityDriver={selectedPriorityDriver}
            setSelectedPriorityDriver={(value: string) => {
              setSelectedPriorityDriver(value);
              try {
                localStorage.setItem("SelectedPriorityDriver", value);
              } catch (storageError) {
                console.warn('Failed to save SelectedPriorityDriver setting due to storage quota:', storageError);
              }
            }}
          />
        } />
        {/* @ts-ignore */}
        <Route path="/triage" element={
          <TriagePage projectWorkItemData={projectWorkItemData} priorityDriverOptions={priorityDriverOptions} />
        } />
        {/* @ts-ignore */}
        <Route path="/time-tracking" element={
          <TimeTrackingPage projectWorkItemData={projectWorkItemData} priorityDriverOptions={priorityDriverOptions} />
        } />
        {/* @ts-ignore */}
        <Route path="/work-in-progress" element={
          <WorkInProgressPage userName={userName} setUserName={setUserName} />
        } />
        {useAdvancedMode && (
          /* @ts-ignore */
          <Route path="/time-administration" element={
            <TimeAdministrationPage useAdvancedMode={useAdvancedMode} projectWorkItemData={projectWorkItemData} usersData={usersData} capexOptions={capexOptions} govProjectOptions={govProjectOptions} priorityDriverOptions={priorityDriverOptions} initiativeOptions={initiativeOptions} productOptions={productOptions} investmentClassOptions={investmentClassOptions} />
          } />
        )}
        {/* @ts-ignore */}
        <Route path="*" element={
          <TeamPlanningPage 
            useAdvancedMode={useAdvancedMode} 
            projectWorkItemData={projectWorkItemData} 
            usersData={usersData} 
            capexOptions={capexOptions} 
            govProjectOptions={govProjectOptions} 
            priorityDriverOptions={priorityDriverOptions} 
            demoOptions={demoOptions} 
            iterationsData={iterationsData} 
            productOptions={productOptions} 
            investmentClassOptions={investmentClassOptions}
            selectedPriorityDriver={selectedPriorityDriver}
            setSelectedPriorityDriver={(value: string) => {
              setSelectedPriorityDriver(value);
              try {
                localStorage.setItem("SelectedPriorityDriver", value);
              } catch (storageError) {
                console.warn('Failed to save SelectedPriorityDriver setting due to storage quota:', storageError);
              }
            }}
          />
        } />
      </Routes>
    </>
  );
}

export default AuthenticatedApp;
